/* ==========================================================================
   Home -- this week, open requests, history, and comparing applicants
   ========================================================================== */

import { useState } from 'react';
import { Icon } from '../../components/Icon';
import {
  Avatar, Card, Empty, JobChips, MatchBar, Note, SearchBox, SectionHead, Seg, Stars, matches,
} from '../../components/ui';
import { BOOKINGS, CLEANERS, OCCURRENCES, REQUESTS } from '../../data/seed';
import {
  appliedCount, byId, cleaner, house, houseScheduleFor, jobLabel, liveApplicants, matchScore, shortName,
} from '../../lib/domain';
import { DOW, JOB_CATALOG } from '../../data/catalog';
import {
  TODAY_ISO, addDays, ago, endTime, fmtDate, fmtTime, money, mondayOf, parseDate,
} from '../../lib/time';
import { t } from '../../lib/i18n';
import {
  HOUSE_IDS, chooseApplicant, commit, focusFind, logPastJob, nav, openChat, openSheet,
  renewRequest, shareLink, startEditRequest, startPastJob, state, stopLooking, tab,
} from '../../store';
import { BookingCard, Owed } from '../shared/Bookings';
import { PushNudge } from '../../components/PushCard';

export function Requests() {
  return (
    <div className="screen-pad">
      <PushNudge />
      <Seg
        options={[
          { id: 'week', label: t('thisweek') },
          { id: 'open', label: t('t_myrequests') },
          { id: 'history', label: t('finished') },
        ]}
        value={state.reqMode}
        onChange={(v) => { state.reqMode = v; commit(); }}
      />
      {state.reqMode === 'week' && <Week />}
      {state.reqMode === 'open' && <Open />}
      {state.reqMode === 'history' && <History />}
    </div>
  );
}

/* ------------------------------------------------------------- this week */

function Week() {
  const monday = addDays(mondayOf(TODAY_ISO), state.weekOffset * 7);
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const thisWeek = state.weekOffset === 0;

  /* One property, always: the one selected in the app bar. Two properties
     added together answers a question nobody asked -- you plan one home at a
     time, and a combined total is just a number to mentally unpick. */
  const scope = [state.houseId];
  const all = days.map((iso) => ({
    iso,
    jobs: houseScheduleFor(scope, iso),
    /* Weeks of a repeating job that have not become bookings yet. A home
       planning its month needs to see them, and needs to be able to change
       one without touching the arrangement. */
    repeats: OCCURRENCES.filter(
      (o) => scope.includes(o.houseId) && o.date === iso
        && !houseScheduleFor(scope, iso).some((b) => b.recurringId === o.recurringId),
    ),
  }));
  const count = all.reduce((s, d) => s + d.jobs.length, 0);
  const total = all.reduce(
    (s, d) => s + d.jobs.reduce((x, b) => x + b.rate * b.hours, 0),
    0,
  );

  return (
    <>
      <Card className={thisWeek ? 'tint' : ''}>
        <div className="row between" style={{ marginBottom: 4 }}>
          <button
            type="button"
            className="btn quiet sm"
            onClick={() => { state.weekOffset -= 1; commit(); }}
            aria-label={t('r_prevweek')}
          >
            ‹
          </button>
          <b style={{ fontSize: 13.5 }}>
            {thisWeek ? t('thisweek') : 'Week of ' + fmtDate(monday, { long: true })}
          </b>
          <button
            type="button"
            className="btn quiet sm"
            onClick={() => { state.weekOffset += 1; commit(); }}
            aria-label={t('r_nextweek')}
          >
            ›
          </button>
        </div>

        <div className="row between">
          <div className="col">
            <span className="sub tight">{fmtDate(monday, { long: true })}</span>
            <b style={{ fontSize: 19 }}>
              {count ? count + ' visit' + (count > 1 ? 's' : '') : 'Nobody booked'}
            </b>
          </div>
          <div className="col" style={{ textAlign: 'right' }}>
            <span className="sub tight">{t('r_youpay')}</span>
            <span className="rate" style={{ fontSize: 22 }}>{money(total)}</span>
          </div>
        </div>

        {/* Which home this is, when there is more than one to confuse. The
            switch itself lives in the app bar, where it always has. */}
        {HOUSE_IDS.length > 1 && (
          <span className="sub tight">{house(state.houseId).displayName}</span>
        )}

        {!thisWeek && (
          <button
            type="button"
            className="btn ghost sm"
            onClick={() => { state.weekOffset = 0; commit(); }}
          >
            Back to {t('thisweek').toLowerCase()}
          </button>
        )}
      </Card>

      {all.map((d) => {
        const isToday = d.iso === TODAY_ISO;
        return (
          <Card className={isToday ? 'tint' : 'flat'} key={d.iso}>
            <div className="row between">
              <b style={{ fontSize: 14.5 }}>
                {DOW[parseDate(d.iso).getDay()]} {parseDate(d.iso).getDate()}
                {isToday ? ' · ' + t('today') : ''}
              </b>
              {d.jobs.length === 0 && d.repeats.length === 0 && (
              <span className="sub tight">{t('r_nobodycoming')}</span>
            )}
            </div>
            {d.repeats.map((o) => (
              <div
                className={'row top tap' + (o.skipped ? ' faded' : '')}
                key={o.recurringId + o.date}
                role="button"
                tabIndex={0}
                onClick={() => openSheet({ name: 'week', id: o.recurringId, date: o.date })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') openSheet({ name: 'week', id: o.recurringId, date: o.date });
                }}
              >
                <span className="stat" style={{ width: 64, color: 'var(--tile)' }}>
                  {o.skipped ? '—' : fmtTime(o.start)}
                </span>
                <Avatar p={cleaner(o.cleanerId)} size="sm" />
                <div className="col grow">
                  <b style={{ fontSize: 14 }}>{cleaner(o.cleanerId).name}</b>
                  <span className="sub tight">
                    {o.skipped
                      ? 'No visit this week'
                      : t('everyweek') + ' · ' + o.hours + 'h · ' + money(o.rate * o.hours)}
                    {o.changed && !o.skipped ? ' · changed' : ''}
                  </span>
                </div>
              </div>
            ))}

            {d.jobs.map((b) => (
              <div
                className="row top tap"
                key={b.id}
                role="button"
                tabIndex={0}
                onClick={() => nav('h_booking', { id: b.id })}
                onKeyDown={(e) => { if (e.key === 'Enter') nav('h_booking', { id: b.id }); }}
              >
                <span className="stat" style={{ width: 64, color: 'var(--tile)' }}>
                  {fmtTime(b.start)}
                </span>
                <Avatar p={cleaner(b.cleanerId)} size="sm" />
                <div className="col grow">
                  <b style={{ fontSize: 14 }}>{cleaner(b.cleanerId).name}</b>
                  <span className="sub tight">
                    {b.hours}h · {money(b.rate * b.hours)}
                  </span>
                </div>
              </div>
            ))}
          </Card>
        );
      })}
    </>
  );
}

/* ---------------------------------------------------------------- open */

function Open() {
  const mine = REQUESTS.filter((r) => HOUSE_IDS.includes(r.houseId) && r.status === 'open');
  /* Confirmed is not the same as upcoming. A visit whose day has gone and
     was never marked finished was sitting under "Upcoming" indefinitely,
     which is what Oster saw. It is its own state: something happened, or did
     not, and somebody needs to say which. */
  const confirmed = BOOKINGS.filter(
    (b) => HOUSE_IDS.includes(b.houseId) && b.status === 'confirmed',
  );
  const upcoming = confirmed
    .filter((b) => b.date >= TODAY_ISO)
    .sort((a, b) => a.date.localeCompare(b.date));
  const overdue = confirmed
    .filter((b) => b.date < TODAY_ISO)
    .sort((a, b) => b.date.localeCompare(a.date));

  return (
    <>
      <SectionHead
        title={t('t_myrequests')}
        action={t('b_add')}
        onAction={() => tab('h_post')}
      />
      {mine.length ? (
        <div className="cardgrid roomy">
        {mine.map((r) => {
          const live = liveApplicants(r);
          return (
            <Card key={r.id} recur={!!r.recurring} onClick={() => nav('h_request', { id: r.id })}>
              <div className="row between">
                <div className="col">
                  <b className="name">
                    {fmtDate(r.date, { long: true })} · {fmtTime(r.start)}
                  </b>
                  <span className="sub">
                    {r.hours} {t('hours')} at {money(r.rate)}{t('perhour')} ·{' '}
                    {money(r.rate * r.hours)}
                  </span>
                  <span className="sub tight">{house(r.houseId).displayName}</span>
                </div>
                <span className={'chip' + (appliedCount(r) ? ' warm' : '')}>
                  {appliedCount(r) ? appliedCount(r) + ' ' + t('applied') : 'Nobody yet'}
                </span>
              </div>
              <div className="chips">
                {!r.recurring && <span className="chip outline">{t('onetime')}</span>}
                <JobChips ids={r.jobIds} max={4} />
              </div>
              <div className="divider" />
              <div className="row">
                {live.slice(0, 4).map((a) => (
                  <Avatar p={cleaner(a.cleanerId)} size="sm" key={a.cleanerId} />
                ))}
                <span className="sub tight grow">
                  {r.date < TODAY_ISO
                    ? 'That date has passed — open it to post it again'
                    : live.length
                      ? 'Tap to compare and choose'
                      : 'Waiting · posted ' + ago(r.posted)}
                </span>
              </div>
            </Card>
          );
        })}
        </div>
      ) : (
        <Empty
          big={t('r_nothingposted')}
          sub={t('r_invisible')}
          action={t('f_postneed')}
          onAction={() => tab('h_post')}
        />
      )}

      {overdue.length > 0 && (
        <>
          <SectionHead title={t('h_needsfinishing')} />
          <Note warm>
            {t('n_needsfinishing')}
          </Note>
          <div className="cardgrid">
            {overdue.map((b) => <BookingCard key={b.id} b={b} side="house" />)}
          </div>
        </>
      )}

      <SectionHead title={t('upcoming')} />
      {upcoming.length ? (
        <div className="cardgrid">
          {upcoming.map((b) => <BookingCard key={b.id} b={b} side="house" />)}
        </div>
      ) : (
        <span className="sub" style={{ padding: '0 4px' }}>{t('n_nothingbooked')}</span>
      )}
    </>
  );
}

/* -------------------------------------------------------------- history */

function History() {
  const past = BOOKINGS.filter(
    (b) => HOUSE_IDS.includes(b.houseId) && b.status === 'completed',
  );
  return (
    <>
      <Card className="warm prompt">
        <b>{t('r_before')}</b>
        <span className="sub">
          Log the job you already did together. They can be reviewed straight away, which is
          how a good cleaner arrives here with a reputation instead of a blank profile.
        </span>
        <button type="button" className="btn warm block" onClick={startPastJob}>
          {t('t_pastjob')}
        </button>
      </Card>

      <SectionHead title={t('finished')} />
      {past.length ? (
        <>
          <Owed jobs={past} side="house" />
          <div className="cardgrid">
            {past.map((b) => <BookingCard key={b.id} b={b} side="house" />)}
          </div>
        </>
      ) : (
        <Empty big={t('nothing')} sub={t('r_nofinished')} />
      )}
    </>
  );
}

/* ------------------------------------------------------- one request */

export function RequestDetail() {
  const r = byId(REQUESTS, state.params.id!)!;
  const scored = liveApplicants(r)
    .map((a) => ({ a, c: cleaner(a.cleanerId), m: matchScore(cleaner(a.cleanerId), r, 'house') }))
    .sort((x, y) => y.m.score - x.m.score);
  /* Who said yes -- applied or countered -- and can be booked; and who was
     only invited and has not answered. Kept apart so an invitation can never
     look like an application. */
  const said = scored.filter(({ a }) => a.status === 'applied' || a.status === 'counter');
  const waiting = scored.filter(({ a }) => a.status === 'invited');

  return (
    <div className="screen-pad">
      <Card recur={!!r.recurring}>
        <SectionHead title={t('h_yourrequest')} />
        <div className="row between">
          <span className="sub">{t('when')}</span>
          <b>
            {fmtDate(r.date, { long: true })}, {fmtTime(r.start)}–{endTime(r.start, r.hours)}
          </b>
        </div>
        <div className="row between">
          <span className="sub">Offering</span>
          <span className="rate">
            {money(r.rate)}
            <small>{t('perhour')} × {r.hours}h = {money(r.rate * r.hours)}</small>
          </span>
        </div>
        <div className="row between">
          <span className="sub">Property</span>
          <b>{house(r.houseId).displayName}</b>
        </div>
        <div className="chips"><JobChips ids={r.jobIds} /></div>
        {r.status === 'open' && (
          <button
            type="button"
            className="btn quiet block"
            onClick={() => void shareLink('request', r.id)}
          >
            {t('r_sharejob')}
          </button>
        )}
        {r.notes && <Note>&quot;{r.notes}&quot;</Note>}

        {/* The date has gone and nobody came. Retyping a request you already
            wrote, because a day passed, is work the app should be doing. */}
        {r.status === 'open' && r.date < TODAY_ISO && (
          <Note warm>
            <b>This date has passed{scored.length === 0 ? ' and nobody applied' : ''}.</b>{' '}
            Post the same job for another day — everything you wrote is kept.
            {r.renewals > 0 && (
              <>
                {' '}It has already been moved {r.renewals}{' '}
                {r.renewals === 1 ? 'time' : 'times'}
                {r.renewals >= 3 ? ', which usually means the rate or the hours want another look.' : '.'}
              </>
            )}
            <div className="row" style={{ gap: 8, marginTop: 9 }}>
              <button
                type="button"
                className="btn warm grow sm"
                disabled={state.busy}
                onClick={() => void renewRequest(r.id)}
              >
                {t('r_tomorrow')}
              </button>
              <button
                type="button"
                className="btn ghost grow sm"
                onClick={() => startEditRequest(r.id)}
              >
                {t('r_pickdate')}
              </button>
            </div>
          </Note>
        )}

        <div className="row" style={{ gap: 9 }}>
          <button
            type="button"
            className="btn ghost grow sm"
            onClick={() => startEditRequest(r.id)}
          >
            {t('b_edit')}
          </button>
          <button
            type="button"
            className="btn quiet grow sm"
            onClick={() => stopLooking(r.id)}
          >
            {t('r_stoplooking')}
          </button>
        </div>
      </Card>

      <button type="button" className="btn ghost block" onClick={() => focusFind(r.id)}>
        <Icon name="search" /> Browse cleaners available on {fmtDate(r.date)}
      </button>

      <SectionHead
        title={said.length + ' ' + (said.length === 1 ? 'cleaner' : 'cleaners') + ' ' + t('applied')}
      />

      {said.length === 0 && waiting.length === 0 && (
        <Empty
          big={t('r_noapplicants')}
          sub={t('r_firsthours')}
          action="Invite someone directly"
          onAction={() => focusFind(r.id)}
        />
      )}

      {said.length === 0 && waiting.length > 0 && (
        <span className="sub" style={{ padding: '0 4px' }}>
          Nobody has said yes yet. You can book a cleaner once they accept.
        </span>
      )}

      {said.map(({ a, c, m }, i) => (
        <div className={'applicant' + (i === 0 && said.length > 1 ? ' best' : '')} key={c.id}>
          {i === 0 && said.length > 1 && (
            <span className="chip tile">{t('r_bestfit')}</span>
          )}
          <div className="row top">
            <Avatar p={c} />
            <div className="col grow">
              <div className="row between">
                <b className="name">{c.name}</b>
                <span className="rate">
                  {money(a.counterRate || r.rate)}<small>{t('perhour')}</small>
                </span>
              </div>
              <span className="sub">{c.headline}</span>
              <div className="row" style={{ gap: 6, marginTop: 3 }}>
                <Stars value={c.ratings.overall} />
                <span className="sub tight">{c.ratings.count} {t('reviews')}</span>
              </div>
            </div>
          </div>

          {a.counterRate && (
            <Note warm>
              <b>Countered at {money(a.counterRate)}{t('perhour')}.</b>{' '}
              {money(a.counterRate - r.rate)}{t('perhour')} more than you offered,{' '}
              {money(a.counterRate * r.hours)} for the {r.hours} hours.
            </Note>
          )}
          {a.message && <span className="sub">&quot;{a.message}&quot;</span>}

          <MatchBar score={m.score} />
          <span className="sub tight">{m.reasons.slice(0, 3).join(' · ')}</span>
          <div className="chips">
            <JobChips ids={r.jobIds.filter((j) => c.specialties.includes(j))} max={3} />
          </div>

          <div className="row" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn grow sm"
              onClick={() => chooseApplicant(r.id, c.id)}
            >
              {t('b_choose')} {shortName(c.name)}
            </button>
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => openChat({ cleanerId: c.id, houseId: r.houseId })}
            >
              {t('b_message')}
            </button>
          </div>
        </div>
      ))}

      {/* Invited, not yet answered. No Choose button: an invitation is a
          question, and only the cleaner's yes makes a booking. */}
      {waiting.length > 0 && (
        <>
          <SectionHead title={'Invited · waiting for an answer'} />
          {waiting.map(({ c }) => (
            <div className="applicant" key={c.id}>
              <div className="row top">
                <Avatar p={c} />
                <div className="col grow">
                  <b className="name">{c.name}</b>
                  <span className="sub">
                    Invited. {shortName(c.name)} has not accepted yet, so they cannot be booked.
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => openChat({ cleanerId: c.id, houseId: r.houseId })}
              >
                {t('b_message')} {shortName(c.name)}
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------- past job log */

export function PastJob() {
  const e = state.pastJob;
  const [find, setFind] = useState('');
  const [jobFind, setJobFind] = useState('');
  if (!e) return null;
  const h = house(state.houseId);
  const set = (k: keyof typeof e, v: unknown) => { (e as any)[k] = v; commit(); };

  /* Once chosen, show only them — a wall of names is no help after the fact. */
  const chosen = e.cleanerId ? CLEANERS.filter((c) => c.id === e.cleanerId) : [];
  const results = e.cleanerId
    ? chosen
    : find.trim()
      ? CLEANERS.filter((c) => matches(c.name + ' ' + c.headline, find)).slice(0, 8)
      : [];

  /* Their own list first, because that is what usually happened, then the
     rest of the catalog. Anything already ticked stays visible however the
     search narrows, so a tick can always be undone. */
  const saved = h.jobList.map((j) => j.jobId);
  const rest = JOB_CATALOG.map((j) => j.id).filter((id) => !saved.includes(id));
  const pastJobChoices = [...saved, ...rest].filter(
    (id) => e.jobIds.includes(id) || matches(jobLabel(id), jobFind),
  ).slice(0, jobFind.trim() ? 40 : saved.length + 6);

  return (
    <div className="screen-pad">
      <Note>
        This does not book anything. It records work that already happened, so you can review
        someone you have used for years and they stop starting from zero.
      </Note>

      <Card>
        <SectionHead
          title={t('h_who')}
          action={e.cleanerId ? 'Change' : undefined}
          onAction={() => { set('cleanerId', ''); setFind(''); }}
        />
        {!e.cleanerId && (
          <SearchBox
            value={find}
            onChange={setFind}
            placeholder={t('r_typename')}
          />
        )}
        <div className="chips">
          {results.map((c) => (
            <button
              key={c.id}
              type="button"
              className={'pick' + (e.cleanerId === c.id ? ' on' : '')}
              onClick={() => set('cleanerId', c.id)}
            >
              {e.cleanerId === c.id && <span className="tick">✓</span>}
              {c.name}
            </button>
          ))}
        </div>
        {!e.cleanerId && find.trim() && !results.length && (
          <span className="hint">
            {t('r_nobodyname')}
          </span>
        )}
        {!e.cleanerId && !find.trim() && (
          <span className="hint">
            {t('r_searchperson')}
          </span>
        )}
      </Card>

      <Card>
        <SectionHead title={t('when')} />
        <div className="field">
          <label>Date</label>
          <input
            className="input"
            type="date"
            max={TODAY_ISO}
            value={e.date}
            onChange={(ev) => set('date', ev.target.value)}
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label>{t('pr_start')}</label>
            <input
              className="input"
              type="time"
              value={e.start}
              onChange={(ev) => set('start', ev.target.value)}
            />
          </div>
          <div className="field">
            <label>{t('hours')}</label>
            <div className="stepper">
              <button type="button" onClick={() => set('hours', Math.max(1, e.hours - 0.5))}>−</button>
              <span className="val">{e.hours}</span>
              <button type="button" onClick={() => set('hours', e.hours + 0.5)}>+</button>
            </div>
          </div>
        </div>
        <div className="field">
          <label>What you paid per hour — {money(e.rate)}</label>
          <div className="slider">
            <input
              type="range"
              min={15}
              max={200}
              value={e.rate}
              onChange={(ev) => set('rate', Number(ev.target.value))}
            />
            <div className="ends"><span>$15</span><span>$200</span></div>
          </div>
        </div>
      </Card>

      <Card>
        <SectionHead
          title={t('h_whattheydid')}
          action={e.jobIds.length ? e.jobIds.length + ' picked' : undefined}
        />
        <SearchBox
          value={jobFind}
          onChange={setJobFind}
          placeholder={t('r_searchjobs')}
        />
        <div className="chips">
          {pastJobChoices.map((jobId) => (
            <button
              key={jobId}
              type="button"
              className={'pick' + (e.jobIds.includes(jobId) ? ' on' : '')}
              onClick={() => {
                const i = e.jobIds.indexOf(jobId);
                if (i >= 0) e.jobIds.splice(i, 1); else e.jobIds.push(jobId);
                commit();
              }}
            >
              {e.jobIds.includes(jobId) && <span className="tick">✓</span>}
              {jobLabel(jobId)}
            </button>
          ))}
        </div>
        {!pastJobChoices.length && (
          <span className="hint">Nothing matches “{jobFind}”.</span>
        )}
        <span className="hint">
          Your saved list first, then anything else from the catalog — a job you did once
          years ago does not have to live on your list forever.
        </span>
      </Card>

      <button
        type="button"
        className="btn block lg"
        disabled={!e.cleanerId || e.jobIds.length === 0}
        onClick={logPastJob}
      >
        {t('r_logreview')}
      </button>
    </div>
  );
}
